import React, { useState, useEffect, useRef } from 'react';
// Inline SVG components to match the vendor form's design
const CloseIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);
const SpinnerIcon = ({ size = 16, className = '' }) => (
  <svg className={`animate-spin ${className}`} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="2" x2="12" y2="6"></line>
    <line x1="12" y1="18" x2="12" y2="22"></line>
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
    <line x1="2" y1="12" x2="6" y2="12"></line>
    <line x1="18" y1="12" x2="22" y2="12"></line>
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
  </svg>
);
const DollarSignIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23"></line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
  </svg>
);
// New Maximize Icon
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
// New Minimize Icon
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);
const API_URL = '/api';
const initialFormData = {
  currency_code: '',
  currency_name: '',
  symbol: '',
  default_rate: '1.0',
  status: 'Active',
  effective_date: '',
  department_id: '' // Added department_id field
};
const CurrencyFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [activeTab, setActiveTab] = useState('currencyInfo');
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false); // New state for maximize/minimize
  
  // Department-related state
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  
  // Refs for potential future file inputs
  const fileInputRefs = useRef({});
  
  // Function to fetch current user
  const fetchCurrentUser = async () => {
    try {
    const token = localStorage.getItem('authToken'); // store token after login
    if (!token) {
      setIsUserValid(false);
      setAuthError('No token found. Please log in again.');
      return false;
    }
    const userRes = await fetch(`${API_URL}/current-user`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    const userData = await userRes.json();
      
      if (userData.user && userData.user.UserID) {
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        console.log('Current user fetched:', userData);
        return true;
      } else {
        console.error('Invalid user data received:', userData);
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page.');
      return false;
    }
  };
  
  // Function to fetch departments
  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const response = await fetch(`${API_URL}/departments`);
      const data = await response.json();
      console.log('Fetched departments:', data);
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };
  
  // Function to fetch screen configuration
  const fetchScreenConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/screens`);
      const data = await response.json();
      console.log('Fetched screen configurations:', data);
      
      // Find the currency screen configuration
      const currencyScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'CurrencyMaster'.toLowerCase()
      );
      
      console.log('Currency screen configuration:', currencyScreen);
      if (currencyScreen) {
        setScreenConfig(currencyScreen);
        // Show department field if ShowOnHeader is 1
        const shouldShowDepartment = currencyScreen.ShowOnHeader === true;
        setShowDepartmentField(shouldShowDepartment);
        
        // If department field should be shown, fetch departments
        if (shouldShowDepartment) {
          fetchDepartments();
        }
      }
    } catch (error) {
      console.error('Error fetching screen configuration:', error);
      setShowDepartmentField(false);
    }
  };
  
  // Toggle maximize/minimize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  useEffect(() => {
    fetchCurrentUser();
  }, []);
  
  useEffect(() => {
    if (open) {
      // Fetch current user when modal opens
      fetchCurrentUser();
      
      // Fetch screen configuration to determine if department field should be shown
      fetchScreenConfig();
      
      if (isEdit && initialData) {
        console.log('Initial data for edit:', initialData);
        
        // Create a copy of initialData without created_by and updated_by
        const { CreatedBy, UpdatedBy, ...dataWithoutUserFields } = initialData;
        
        // Extract values with proper fallbacks for different property name conventions
        const currencyCode = dataWithoutUserFields.CurrencyCode || dataWithoutUserFields.currency_code || '';
        const currencyName = dataWithoutUserFields.CurrencyName || dataWithoutUserFields.currency_name || '';
        const symbol = dataWithoutUserFields.Symbol || dataWithoutUserFields.symbol || '';
        const defaultRate = dataWithoutUserFields.DefaultRate || dataWithoutUserFields.default_rate || '1.0';
        const status = dataWithoutUserFields.Status || dataWithoutUserFields.status || 'Active';
        const effectiveDate = dataWithoutUserFields.EffectiveDate || dataWithoutUserFields.effective_date || '';
        const departmentId = dataWithoutUserFields.DepartmentID || dataWithoutUserFields.department_id || '';
        
        setFormData({
          currency_code: currencyCode,
          currency_name: currencyName,
          symbol: symbol,
          default_rate: defaultRate?.toString() || '1.0',
          status: (status === true || status === 1 || status === '1') ? 'Active' : 'Inactive',
          effective_date: effectiveDate 
                         ? new Date(effectiveDate).toISOString().split('T')[0] 
                         : '',
          department_id: departmentId // Added department_id
        });
      } else {
        // Only fetch new code if creating and code is empty or default
        if (!formData.currency_code || formData.currency_code === initialFormData.currency_code) {
          fetchNextCode();
        }
        setFormData(prev => ({
          ...prev,
          effective_date: new Date().toISOString().split('T')[0],
          status: 'Active',
          department_id: '' // Reset department_id for new entries
        }));
      }
    } else {
      // Reset department-related state when modal closes
      setShowDepartmentField(false);
      setScreenConfig(null);
      setDepartments([]);
    }
  }, [open, initialData, isEdit]);
  
  const fetchNextCode = async () => {
    setIsLoadingCode(true);
    try {
      const response = await fetch(`${API_URL}/currencylatestcode`);
      const data = await response.json();
      setFormData(prev => ({ ...prev, currency_code: data.nextCode }));
    } catch (error) {
      console.error('Error fetching next currency code:', error);
    } finally {
      setIsLoadingCode(false);
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check if we have a valid current user
    if (!isUserValid || !currentUser || !currentUser.UserID) {
      alert(authError || 'User information not available. Please refresh the page and log in again.');
      return;
    }
    
    // Convert default_rate to number for submission
    const dataToSend = {
      ...formData,
      status: formData.status === 'Active' ? 1 : 0,
      default_rate: parseFloat(formData.default_rate) || 1.0
    };
    
    // Only append the relevant user ID field based on operation
    const userId = parseInt(currentUser.UserID);
    console.log('Using user ID:', userId);
    
    if (isEdit && initialData) {
      dataToSend.updated_by = userId;
      console.log('Appended updated_by:', userId);
    } else {
      dataToSend.created_by = userId;
      console.log('Appended created_by:', userId);
    }
    
    // Include department_id only if showDepartmentField is true
    if (showDepartmentField) {
      dataToSend.department_id = formData.department_id;
    }
    
    // Log data for debugging
    console.log('Submitting data:', dataToSend);
    
    onSubmit(dataToSend);
  };
  
  const tabs = [
    { id: 'currencyInfo', name: 'Currency Info' },
  ];

  if (!open) return null;
  
  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out
        ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
    >
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none; /* Internet Explorer 10+ */
            scrollbar-width: none; /* Firefox */
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none; /* Safari and Chrome */
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      
      {/* This is the actual slide-in panel with fixed header and scrollable content */}
      <div 
        className={`bg-white shadow-2xl flex flex-col
          transition-all transform-gpu duration-300 ease-in-out
          ${open ? 'translate-x-0' : 'translate-x-full'}
          ${isMaximized ? 'w-full h-full max-w-none rounded-none' : 'w-full max-w-6xl h-full'}`}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#6A89A7] rounded-full flex items-center justify-center">
                <DollarSignIcon className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#384959]">
                {isEdit ? 'Edit Currency' : 'Create New Currency'}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {/* Maximize/Minimize Button */}
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1 rounded-full hover:bg-[#F1F5F9]" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon /> : <MaximizeIcon />}
              </button>
              {/* Close Button */}
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1 rounded-full hover:bg-[#F1F5F9]" 
                onClick={onClose}
                title="Close"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>
        
        {/* User validation warning */}
        {open && !isUserValid && (
          <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  {authError || 'User information not available. Please refresh the page and log in again.'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Fixed Tabs */}
        <div className="flex-shrink-0 px-8 pb-6">
          <div className="flex border-b border-[#BDDDFC] -mx-8 px-8 overflow-x-auto overflow-y-hidden whitespace-nowrap custom-scrollbar">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-4 px-4 -mb-px text-sm font-medium transition-colors focus:outline-none
                  ${activeTab === tab.id 
                    ? 'border-b-2 border-[#6A89A7] text-[#6A89A7]' 
                    : 'border-b-2 border-transparent text-[#A0AEC0] hover:text-[#6A89A7]'
                  }`}
              >
                {tab.name}
              </button>
            ))}
          </div>
          {/* Custom scrollbar indicator */}
          <div className="flex justify-center mt-2">
            <div className="w-16 h-1 rounded-full bg-[#cbd5e1]"></div>
          </div>
        </div>
        
        {/* Scrollable Content Area */}
         <div className="flex-grow overflow-y-auto custom-scrollbar px-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              {/* Currency Information */}
              {activeTab === 'currencyInfo' && (
                <div className='space-y-4'>
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">Currency Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Currency Code */}
                    <div className='flex flex-col gap-2'>
                      <label className="block text-sm font-medium text-[#64748b]">
                        Currency Code *
                      </label>
                      <input
                        type="text"
                        value={isLoadingCode ? 'Loading...' : formData.currency_code}
                        onChange={(e) => handleInputChange('currency_code', e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                        placeholder="e.g., EUR, USD, INR"
                        required
                      />
                    </div>
                    
                    {/* Currency Name */}
                    <div className='flex flex-col gap-2'>
                      <label className="block text-sm font-medium text-[#64748b]">
                        Currency Name *
                      </label>
                      <input
                        type="text"
                        value={formData.currency_name}
                        onChange={(e) => handleInputChange('currency_name', e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                        placeholder="e.g., US Dollar"
                        required
                      />
                    </div>
                    
                    {/* Symbol */}
                    <div className='flex flex-col gap-2'>
                      <label className="block text-sm font-medium text-[#64748b]">
                        Symbol *
                      </label>
                      <input
                        type="text"
                        value={formData.symbol}
                        onChange={(e) => handleInputChange('symbol', e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                        placeholder="e.g., $"
                        required
                        maxLength={5}
                      />
                    </div>
                    
                    {/* Default Rate */}
                    <div className='flex flex-col gap-2'>
                      <label className="block text-sm font-medium text-[#64748b]">
                        Default Rate
                      </label>
                      <input
                        type="number"
                        step="0.0001"
                        value={formData.default_rate}
                        onChange={(e) => handleInputChange('default_rate', e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                        placeholder="1.0000"
                        min="0"
                      />
                    </div>

                    {/* Department Selection Field - Only shown if ShowOnHeader is 1 */}
                    {showDepartmentField && (
                      <div className='flex flex-col gap-2 '>
                        <label className="block text-sm font-medium text-[#64748b]">
                          Department *
                        </label>
                        <select
                          name="department_id"
                          value={formData.department_id}
                          onChange={(e) => handleInputChange('department_id', e.target.value)}
                          className={`w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase`}
                          disabled={loadingDepartments}
                        >
                          <option value="">{loadingDepartments ? 'Loading departments...' : 'Select Department'}</option>
                          {departments.map((dept) => (
                            <option key={dept.DepartmentID} value={dept.DepartmentID}>
                              {dept.DepartmentCode} - {dept.DepartmentName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Status */}
                    <div className='flex flex-col gap-2'>
                      <label className="block text-sm font-medium text-[#64748b]">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                    
                    {/* Effective Date */}
                    <div className='flex flex-col gap-2'>
                      <label className="block text-sm font-medium text-[#64748b]">
                        Effective Date
                      </label>
                      <input
                        type="date"
                        value={formData.effective_date}
                        onChange={(e) => handleInputChange('effective_date', e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                      />
                    </div>
                    
                    
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
        
        {/* Fixed Footer with Buttons */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button 
              type="button" 
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="flex items-center gap-2 px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#5a7997]" 
              disabled={loading || !isUserValid}
              onClick={handleSubmit}
            >
              {loading ? (
                <>
                  <SpinnerIcon />
                  Saving...
                </>
              ) : (
                <>
                  <SaveIcon />
                  {isEdit ? 'Update & Save' : 'Continue & Save'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
export default CurrencyFormModal;